import { addDays, eachDayOfInterval, endOfMonth, endOfWeek, format, getDefaultOptions, getISOWeek, getWeek, isAfter, isBefore, parseISO, startOfMonth, startOfWeek, } from "date-fns";
import { weekStartsOn$ } from "../state/weekStartsOn";
import { formatWeekDateRange as formatWeekDateRangeFull, getFiscalWeekStartKeyForDate, getFiscalWeekWindowForDateKey, } from "./forecastTableWeekUtils";
import { i18n } from "./language/i18n";
import { getCurrentLocale } from "./language/language";
export function formatWeekDateRange(weekStartDate) {
    return formatWeekDateRangeFull(weekStartDate, weekStartsOn$.value);
}
export const FISCAL_YEAR_START_MM_DD = "09-01";
export const FISCAL_YEAR_END_MM_DD = "08-31";
export const FISCAL_YEAR_MILLENNIUM = 2000;
export const FISCAL_YEAR_START_MONTH = 8;
export const FISCAL_YEAR_END_MONTH = 7;
export const FISCAL_YEAR_END_DAY = 31;
export function computeForecastBounds() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentFiscalYear = currentMonth >= 9 ? currentYear + 1 : currentYear;
    const prevFiscalYear = currentFiscalYear - 1;
    const nextFiscalYear = currentFiscalYear + 1;
    const minDate = `${prevFiscalYear - 1}-09-01`;
    const maxDate = `${nextFiscalYear}-08-31`;
    return {
        minDate,
        maxDate,
        allowedFiscalYears: [prevFiscalYear, currentFiscalYear, nextFiscalYear],
    };
}
export const getCurrentFiscalYear = () => {
    return getFiscalYearFullFromDate(new Date());
};
export const formatDateDisplay = (dateString) => {
    if (dateString === undefined || dateString === "")
        return "";
    const date = parseISO(dateString);
    return date.toLocaleDateString(i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};
export const formatDateShort = (dateString) => {
    if (dateString === undefined || dateString === "")
        return "";
    const date = parseISO(dateString);
    return date.toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
    });
};
export const addDaysToDate = (dateString, days) => {
    if (!dateString)
        return "";
    const parsed = parseISO(dateString);
    if (Number.isNaN(parsed.getTime()))
        return dateString;
    return format(addDays(parsed, days), "yyyy-MM-dd");
};
export const isDateWithinForecastRange = (dateString) => {
    if (!dateString)
        return false;
    const date = parseISO(dateString);
    if (Number.isNaN(date.getTime()))
        return false;
    const { minDate, maxDate } = computeForecastBounds();
    const min = parseISO(minDate);
    const max = parseISO(maxDate);
    return !isBefore(date, min) && !isAfter(date, max);
};
export const doesWeekOverlapForecastRange = (dateInWeek) => {
    if (!dateInWeek)
        return false;
    const { weekStartKey, weekEndKey } = getFiscalWeekWindowForDateKey(dateInWeek, weekStartsOn$.value);
    const start = parseISO(weekStartKey);
    const end = parseISO(weekEndKey);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return false;
    const { minDate, maxDate } = computeForecastBounds();
    const min = parseISO(minDate);
    const max = parseISO(maxDate);
    return !isBefore(end, min) && !isAfter(start, max);
};
export const doesMonthOverlapForecastRange = (dateInMonth) => {
    const monthStart = startOfMonth(dateInMonth);
    const monthEnd = endOfMonth(dateInMonth);
    const { minDate, maxDate } = computeForecastBounds();
    const min = parseISO(minDate);
    const max = parseISO(maxDate);
    return !isBefore(monthEnd, min) && !isAfter(monthStart, max);
};
export const isHistoricalDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
};
const parseDateAtMidnight = (dateString) => {
    if (dateString === "")
        return null;
    const date = parseISO(dateString);
    if (Number.isNaN(date.getTime()))
        return null;
    date.setHours(0, 0, 0, 0);
    return date;
};
export const isForecastSlotEditable = (view, slotKey, referenceDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (view === "day") {
        const selectedDate = parseDateAtMidnight(referenceDate);
        return selectedDate !== null && selectedDate > today;
    }
    if (view === "week") {
        const slotDate = parseDateAtMidnight(slotKey);
        return slotDate !== null && slotDate > today;
    }
    if (view === "year") {
        const normalizedSlotKey = slotKey.slice(0, 10);
        if (normalizedSlotKey === "")
            return false;
        const currentWeekStartKey = getFiscalWeekStartKeyForDate(today, weekStartsOn$.value);
        return normalizedSlotKey > currentWeekStartKey;
    }
    return false;
};
export const getWeekRange = (dateInWeek) => {
    const locale = getCurrentLocale();
    const { weekStartKey, weekEndKey } = getFiscalWeekWindowForDateKey(dateInWeek, weekStartsOn$.value);
    const start = parseISO(weekStartKey);
    const end = parseISO(weekEndKey);
    const startLabel = format(start, "dd MMM", { locale });
    const endLabel = format(end, "dd MMM", { locale });
    return {
        start: startLabel,
        end: endLabel,
    };
};
export function formatWeekdayDay(dateString) {
    if (!dateString)
        return "";
    const parsed = parseISO(dateString);
    const weekday = parsed.toLocaleDateString(i18n.language, {
        weekday: "long",
    });
    const dayNum = parsed.toLocaleDateString(i18n.language, {
        day: "numeric",
    });
    return `${weekday} ${dayNum}`;
}
export function formatWeekdayShort(dateString) {
    if (!dateString)
        return "";
    const parsed = parseISO(dateString);
    const weekdayShort = parsed.toLocaleDateString(i18n.language, {
        weekday: "short",
    });
    const dayNum = parsed.toLocaleDateString(i18n.language, {
        day: "numeric",
    });
    return `${weekdayShort} ${dayNum}`;
}
export function getLocalWeekNumber(dateString) {
    const parsed = parseISO(dateString);
    const { weekStartsOn = 1 } = getDefaultOptions();
    return weekStartsOn === 1 ? getISOWeek(parsed) : getWeek(parsed);
}
export function formatYearWeekHeader(weekStartDate) {
    if (!weekStartDate)
        return "";
    const weekNum = getLocalWeekNumber(weekStartDate);
    const dn = new Intl.DisplayNames(i18n.language, {
        type: "dateTimeField",
        style: "narrow",
    });
    const weekLabel = (dn.of("weekOfYear") ?? "").toUpperCase();
    return `${weekLabel}${weekNum}`;
}
export function getRawValueAt(raw, index) {
    if (!Array.isArray(raw))
        return 0;
    const v = raw[index];
    return typeof v === "number" && Number.isFinite(v) ? v : 0;
}
export function getFiscalYearFullFromDate(date) {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return month >= 9 ? year + 1 : year;
}
export function getFiscalYearSelectionDate(fy) {
    const currentFiscalYear = getCurrentFiscalYear();
    if (fy === currentFiscalYear) {
        return format(new Date(), "yyyy-MM-dd");
    }
    if (fy < FISCAL_YEAR_MILLENNIUM || fy > FISCAL_YEAR_MILLENNIUM + 100) {
        return format(new Date(), "yyyy-MM-dd");
    }
    return format(new Date(fy - 1, FISCAL_YEAR_START_MONTH, 1), "yyyy-MM-dd");
}
export function generateCalendarMonth(date) {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
}
export function getWeekdayLabels(weekStartsOnValue, locale) {
    const baseDate = new Date(Date.UTC(2017, 0, 1));
    const weekStartDate = new Date(baseDate);
    weekStartDate.setUTCDate(baseDate.getUTCDate() + weekStartsOnValue);
    const labels = [];
    for (let i = 0; i < 7; i++) {
        const day = new Date(weekStartDate);
        day.setUTCDate(weekStartDate.getUTCDate() + i);
        labels.push(day.toLocaleString(locale, {
            weekday: "short",
        }));
    }
    return labels;
}
export function getFiscalYearFromDate(dateString) {
    const year = parseInt(dateString.slice(0, 4), 10);
    const month = parseInt(dateString.slice(5, 7), 10);
    const fiscalStartCalendarYear = month >= 9 ? year + 1 : year;
    const lastTwoDigits = fiscalStartCalendarYear % 100;
    return lastTwoDigits;
}
export function getFiscalYearPeriod(fiscalYear) {
    const fYYear = fiscalYear + FISCAL_YEAR_MILLENNIUM;
    const fyStart = new Date(fYYear - 1, 8, 1);
    const fyEnd = new Date(fYYear, 7, 31);
    const startingWeek = getWeek(fyStart);
    return { fyStart, fyEnd, startingWeek };
}
export function getFiscalYearDetails(fiscalYear) {
    const { fyStart, fyEnd, startingWeek } = getFiscalYearPeriod(fiscalYear);
    const weeksInRange = [];
    let cursor = startOfWeek(fyStart);
    while (!isAfter(cursor, fyEnd)) {
        weeksInRange.push(getWeek(cursor));
        cursor = addDays(cursor, 7);
    }
    const weeksInFY = weeksInRange.length;
    return {
        selectedFiscalYear: fiscalYear,
        fyStart,
        fyEnd,
        weeksInFY,
        startingWeek,
    };
}
//# sourceMappingURL=forecastDateUtils.js.map